import get from 'lodash/get';
import find from 'lodash/find';
import groupBy from 'lodash/groupBy';
import sortBy from 'lodash/sortBy';
import merge from 'lodash/merge';
import moment from 'moment';
import DashboardWidgetController from 'dashboard/components/dashboard-widget/dashboard-widget-controller';
import {
  isChainHeadSelector,
  isSuperChainSelector,
  isChainSubordinateSelector,
  isIndependentSelector,
  isStoreProfileSelector,
} from 'selectors';
import { getChartColors } from 'app/actions/theme.actions';
import template from './my-instant-ticket-sales-widget.html';
import chartConfig from './my-instant-ticket-sales-chart.conf.json';
import fetchInstatTicketSales from '../../../resources/my-instant-ticket-sales.api';

const mapStateToThis = ({ session, dictionary, theme }) => {
  const { locale, retailerType, retailer } = session;
  const isStoreProfile = isStoreProfileSelector({ session });
  const isSuperChain = isSuperChainSelector({ session });
  const isChainHead = isChainHeadSelector({ session });
  const isChainSubordinate = isChainSubordinateSelector({ session });
  const isIndependent = isIndependentSelector({ session });
  const terms = dictionary.terms[locale];

  return {
    locale,
    terms,
    theme,
    retailerType,
    isStoreProfile,
    isSuperChain,
    isChainHead,
    isChainSubordinate,
    isIndependent,
    retailer,
  };
};

const mapDispatchToThis = {};

// eslint-disable-next-line no-undef
@Inject('$scope', '$ngRedux', 'gettextCatalog', '$filter', '$state')
class MyInstantTicketSalesWidgetController extends DashboardWidgetController {
  constructor() {
    super();
    this.unsubscribe = this.$ngRedux.connect(mapStateToThis, mapDispatchToThis)(this);
  }

  $onInit() {
    this.isEmpty = false;
    this.currentYear = new Date().getFullYear();
    this.dataType = 1;
    this.colors = getChartColors(this.theme.colors);
    this.retailerType = this.retailerType || this.getRetailerType();
    this.chartConfig = this.extendChartConfig(chartConfig.line);
    this.retailerId = this.$state.params.retailerNo || this.retailer.retailerId;
    this.loadChartData();
  }

  $onDestroy() {
    this.unsubscribe();
  }

  getRetailerType() {
    if (this.isChainHead) {
      return 'CHAIN_HEAD';
    }
    if (this.isSuperChain) {
      return 'SUPER_CHAIN';
    }
    if (this.isChainSubordinate) {
      return 'CHAIN_SUBORDINATE';
    }
    if (this.isIndependent) {
      return 'INDEPENDENT';
    }
  }

  loadChartData() {
    this.thisYearSalesData = [];
    this.prevYearSalesData = [];
    this.zipData = [];
    this.businessData = [];
    this.getData();
    // this.loadDataAsync('my-instant-ticket-sales', this.getData.bind(this), 'myInstantTicketSales');
  }

  calculateDifference(current, last, currentField, lastField) {    
    if(!lastField)
      lastField = currentField;

    if (!last[lastField] && !current[currentField]) {
      return;
    }
    if(!last[currentField]){
      return 100;
    }
    let difference = ((current[currentField] - last[lastField]) / last[lastField]) * 100;
    return isNaN(difference) ? null : difference;
  }

  getData() {
    return fetchInstatTicketSales(this.retailerId)
      .then((results = []) => {
        const data = results;

        this.checkIfEmptyData(data);
        
        if (!data || !data.length) {
          this.isEmpty = true;
        } else {
          this.salesFilteredBy = data[0].salesFilteredBy;
          this.dataTypes = this.isStoreProfile ? this.getSTORE_PROFILEData() : this[`get${this.retailerType}Data`]();
          const years = groupBy(sortBy(data, 'yearNumber'), 'yearNumber');          
          const values = Object.keys(years).map((key) => years[key]);
          if (values.length === 1) {
            this.thisYearSalesData = values[0];
          } else if (values.length > 1) {
            this.prevYearSalesData = values[0];
            this.thisYearSalesData = values[1];
            this.thisYearSalesData.forEach((item) => {
              const prevValue = find(this.prevYearSalesData, { weekInYear: item.weekInYear });
              if (prevValue) {
                item.comparision = {
                  retValAmt: this.calculateDifference(item, prevValue, 'retValAmt'),
                };
                prevValue.comparision = {
                  retValAmt: this.calculateDifference(prevValue, item, 'retValAmt'),
                };
              }              
            });
            
          }
          this.setChartType();
        }
      })
      .then(() => (this.isLoading = false));
  }

  checkIfEmptyData(data) {
    this.isEmpty = data.length === 0;
  }

  getSalesByZipLabel() {

    switch(this.salesFilteredBy) {
      case "County":
        return this.gettextCatalog.getString('Avg. sales of retailers in your county');
      case "City":
        return this.gettextCatalog.getString('Avg. sales of retailers in your city')
      default:
        return this.gettextCatalog.getString('Avg. sales of retailers in your ZIP code')
      }
  }

  setChartType() {
    const option = this.dataTypes.find((opt) => opt.id === this.dataType);    
    option.getData();
  }

  getCHAIN_HEADData() {
    return [
      {
        id: 1,
        label: this.gettextCatalog.getString("Last year's sales"),
        getData: () => this.getLastYearSales(),
      },
    ];
  }

  getSUPER_CHAINData() {
    return [
      {
        id: 1,
        label: this.gettextCatalog.getString("Last year's sales"),
        getData: () => this.getLastYearSales(),
      },
    ];
  }

  getSTORE_PROFILEData() {
    return [
      {
        id: 1,
        label: this.gettextCatalog.getString("Last year's sales"),
        getData: () => this.getLastYearSales(),
      },
      {
        id: 2,
        label: this.getSalesByZipLabel(),
        getData: () => this.getAvgSalesByZip(),
      },
      {
        id: 3,
        label: this.gettextCatalog.getString('Avg. sales of retailers for your business type in state'),
        getData: () => this.getAvgSalesByState(),
      },
      {
        id: 4,
        label: this.gettextCatalog.getString('Avg. sales of retailers in your chain in state'),
        getData: () => this.getAvgSalesByChain(),
      },
    ];
  }

  getCHAIN_SUBORDINATEData() {
    return [
      {
        id: 1,
        label: this.gettextCatalog.getString("Last year's sales"),
        getData: () => this.getLastYearSales(),
      },
      {
        id: 2,
        label: this.getSalesByZipLabel(),
        getData: () => this.getAvgSalesByZip(),
      },
      {
        id: 3,
        label: this.gettextCatalog.getString('Avg. sales of retailers for your business type in state'),
        getData: () => this.getAvgSalesByState(),
      },
      {
        id: 4,
        label: this.gettextCatalog.getString('Avg. sales of retailers in your chain in state'),
        getData: () => this.getAvgSalesByChain(),
      },
    ];
  }

  getINDEPENDENTData() {
    return [
      {
        id: 1,
        label: this.gettextCatalog.getString("Last year's sales"),
        getData: () => this.getLastYearSales(),
      },
      {
        id: 2,
        label: this.getSalesByZipLabel(),
        getData: () => this.getAvgSalesByZip(),
      },
      {
        id: 3,
        label: this.gettextCatalog.getString('Avg. sales of retailers for your business type in state'),
        getData: () => this.getAvgSalesByState(),
      },
    ];
  }

  setColors(data) {
    const colors = this.colors.slice();
    return data.map((item) => {
      item.color = colors.shift();
      return item;
    });
  }

  extendChartConfig(chartConf) {
    const currencySymbol = undefined;
    const currencyFractionSize = 0;    
    return merge(chartConf, {
      chart: {
        dispatch: {
          renderEnd() {
            // HACK: RWZ-10834
            setTimeout(() => {
              document.querySelectorAll('path.nv-point.hover').forEach((item) => item.classList.remove('hover'));
            }, 0);
          },
        },
        tooltip: {
          contentGenerator: (e) => {
            const series = e.series[0];
            if (series.value === null) return;
            const endOfWeekDate = moment.utc(get(e, 'point.item.date'));
            const comparision = get(e, 'point.comparision');
            const header = `
                            <thead>
                              <tr>
                                 <td class="key"><strong>${this.gettextCatalog.getString('Week ending')}</strong></td>
                                 <td class="x-value"><strong>${endOfWeekDate.format('MMM')}. ${endOfWeekDate.format('D')}</strong></td>
                              </tr>
                            </thead>`;

            const row = `
                            <tr>
                              <td class="legend-color-guide"><div style="background-color: ${series.color}"></div></td>
                                 <td class="key">
                                    <strong>
                                       ${this.$filter('currency')(series.value, currencySymbol, currencyFractionSize)}
                                    </strong>
                                 </td>
                            </tr>`;
            
            const secondRow =
              comparision !== undefined
                ? `
                            <tr class="${comparision >= 0 ? 'success' : 'danger'}">
                                 <td><i class="fa ${comparision >= 0 ? 'fa-level-up' : 'fa-level-down'}"></i></td>
                                 <td><strong>${comparision.toFixed(2)}%</strong></td>
                            </tr>`
                : '';

            return `<table>
                            ${header}
                            <tbody>
                            ${row}
                            ${secondRow}
                            </tbody>
                            </table>`;
          },
        },
        yAxis: {
          // eslint-disable-next-line
          tickFormat: (tickVal) => {
            return this.$filter('currency')(tickVal, currencySymbol, currencyFractionSize);
          },
        },
      },
    });
  }

  getYearSalesSeries(data, field) {
    const sortedData = sortBy(data, 'weekInYear');
    return {
      key: sortedData[0].yearNumber,
      values: sortedData.map((item) => {
        const value = {
          x: item.weekInYear,
          y: item[field],
          item,
        };
        if (item.comparision) {
          value.comparision = item.comparision[field];
        }
        return value;
      }),
    };
  }

  getLastYearSales() {
    let formattedData = [];
    if (this.prevYearSalesData.length) {
      formattedData = formattedData.concat(this.getYearSalesSeries(this.prevYearSalesData, 'retValAmt'));
    }

    if (this.thisYearSalesData.length) {
      formattedData = formattedData.concat(this.getYearSalesSeries(this.thisYearSalesData, 'retValAmt'));
    }
    this.redrawChart(formattedData);
  }

  getOtherSalesSeries(data, field) {
    const sortedData = sortBy(data, 'weekInYear');
    const title = field === 'retValAmt' ? this.gettextCatalog.getString('Your Sales') : this.gettextCatalog.getString('Avg. Sales');
    return {
      key: title,
      values: sortedData.map((item) => ({  
          x: item.weekInYear,
          y: item[field],
          item,
        })
      ),
    };
  }

  getAvgSalesByZip() {
    this.getAvgSalesByType('postalWeeklyAvg');
  }

  getAvgSalesByState() {
    this.getAvgSalesByType('sicWeeklyAvg');
  }

  getAvgSalesByChain() {
    this.getAvgSalesByType('chainWeeklyAvg');
  }

  getAvgSalesByType(type) {
    let formattedData = [];

    if (this.thisYearSalesData.length) {
      
      const avgSales = this.getOtherSalesSeries(this.thisYearSalesData, type);
      const current = this.getOtherSalesSeries(this.thisYearSalesData, 'retValAmt');
      
      current.values.forEach((currentPoint) => {
        const prevValue = avgSales.values.find((avgSale) => avgSale.item.weekInYear === currentPoint.item.weekInYear)
        if(prevValue) {
          currentPoint.comparision = this.calculateDifference( currentPoint.item,prevValue.item, 'retValAmt', type);
          prevValue.comparision = this.calculateDifference( prevValue.item,currentPoint.item, type, 'retValAmt');
        } 
      });

      formattedData = formattedData.concat(
        avgSales,
        current
      );      
    }    

    this.redrawChart(formattedData);
  }

  redrawChart(data) {
    this.chartData = this.setColors(data);
    if (this.chartApi) {
      this.chartApi.updateWithData(this.chartData);
      this.chartRefreshTimeout = setTimeout(() => {
        this.chartApi.refresh();
      }, 100);
    }
  }

  $onDestroy() {
    if (this.chartRefreshTimeout) {
      clearTimeout(this.chartRefreshTimeout);
    }
  }
}

export default class MyInstantTicketSalesWidgetComponent {
  constructor() {
    this.template = template;
    this.controller = MyInstantTicketSalesWidgetController;
    this.bindings = {
      model: '<',
      config: '<',
      appCtrl: '<',
      widgetsApi: '<',
      dashboardApi: '<',
      isEditMode: '<',
      isDisabledShare: '<',
      isRemovable: '<',
      removeWidget: '&?',
      setWidgetLoading: '&',
    };
  }
}



// WEBPACK FOOTER //
// ./src/modules/dashboard/components/dashboard-widget/my-instant-ticket-sales/my-instant-ticket-sales-widget-component.js